import { computed, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environment';
import { DashboardOwnerUser } from '@dashboard_models/dashboard-owner-user';
import { CustomerService } from '../customer/customer.service';
import { lastValueFrom } from 'rxjs';
import { HomeService } from '@services/home/home.service';
import { HelperService } from '@services/helper/helper.service';
import { AccountService } from '@services/account/account.service';
import { AW } from '@airwallet/types';
import { SupportTierService } from '@services/support-tier/support-tier.service';
import { AuthService } from '@services/auth/auth.service';
@Injectable({
    providedIn: 'root'
})
export class OnboardingService {
    baseUrl: string = environment.baseUrl;
    user: DashboardOwnerUser = this.helperService.getDashboardOwnerUser();

    customerDetails: Signal<AW.Account.Details | null> = this.homeService.customerDetails;
    subCustomerPermissions: WritableSignal<AW.Account.SubCustomer.Permission> = signal({
        allow_billing: true,
        allow_location: true,
        allow_refund: true,
        allow_change_billing: true,
        allow_advanced_share: true, // used for Ark locations
        lock_advanced_share: false, // used for Ark locations
        allow_coupon: true,
        allow_share: true // used for blocking share settings entirely when currency is not matching between operator and sub-customer
    });
    onboardingState: Signal<AW.Account.OnboardingState | undefined> = computed(() => {
        const customerDetails: AW.Account.Details | null = this.customerDetails();
        return customerDetails?.onboarding_state;
    });
    onboardingComplete: WritableSignal<boolean> = signal(false);
    isLife: Signal<boolean> = computed(() => {
        const customerDetails: AW.Account.Details | null = this.customerDetails();
        return customerDetails?.agreement_type === 'life';
    });

    // Signals that indicate the current state of the onboarding process
    hasCompletedAccountInformation: Signal<boolean> = computed(() => {
        return this.onboardingState()?.external_account ?? false;
    });

    shouldShowKYC: Signal<boolean> = computed(() => {
        return this.isLife();
    });

    shouldShowPlanAndBilling: Signal<boolean> = computed(() => {
        return this.isLife() && this.subCustomerPermissions()?.allow_billing;
    });

    readonly planInterval: Signal<number | null> = computed(() => this.authService.customerFirestore()?.subscription_plan_interval ?? this.accountService.currentSubscriptionPlanInfo()?.subscription_plan_interval ?? null);
    readonly planType: Signal<AW.Account.SubscriptionPlanType | null> = computed(() => this.authService.customerFirestore()?.subscription_plan_type ?? this.accountService.currentSubscriptionPlanInfo()?.subscription_plan_type ?? null);

    hasCompletedPlan: Signal<boolean> = computed(() => {
        const interval = this.planInterval();
        const subscriptionPlanIntervalIsSet: boolean = interval !== null && interval !== 0;
        return (subscriptionPlanIntervalIsSet && this.hasCompletedKYC()) || (!this.shouldShowPlanAndBilling() && this.hasCompletedAccountInformation());
    });

    hasCompletedKYC: Signal<boolean> = computed(() => {
        return this.onboardingState()?.capabilities === 'active' || !this.shouldShowKYC();
    });

    readonly shouldShowSupport: Signal<boolean> = computed(() => {
        return this.shouldShowPlanAndBilling() && this.supportTierService.isAvailable();
    });

    readonly hasCompletedSupport: Signal<boolean> = computed(() => {
        return !this.shouldShowSupport() || !!this.supportTierService.supportTier() || !!this.onboardingState()?.billing;
    });

    nextStepIsBilling: Signal<boolean> = computed(() => {
        return this.hasCompletedPlan() && this.hasCompletedSupport();
    });

    constructor(
        private http: HttpClient,
        private customerService: CustomerService,
        private homeService: HomeService,
        private helperService: HelperService,
        private accountService: AccountService,
        private supportTierService: SupportTierService,
        private authService: AuthService
    ) {}

    async getSubcustomerPermissionsFromSub(): Promise<void> {
        return new Promise(resolve => {
            const subCustomerPermissionSub = this.customerService.readSubCustomerPermission(this.user.uid).subscribe(query => {
                subCustomerPermissionSub.unsubscribe();
                const val = query.snapshot.val();
                if (val) this.subCustomerPermissions.set(val);
                return resolve();
            });
        });
    }

    async getOnboardingStatus(): Promise<void> {
        if (this.user.uid.includes('_operated_by_')) {
            await this.getSubcustomerPermissionsFromSub();
        }
        // Make sure the subscription-plan info is loaded before deriving
        // `hasCompletedPlan()` — otherwise the plan step would read as
        // "not yet started" forever and we'd never mark onboarding complete.
        if (this.shouldShowPlanAndBilling() && !this.accountService.currentSubscriptionPlanInfo()) {
            await this.accountService.getSubscriptionPlanInfo();
        }

        // Compose from the per-step signals the onboarding UI actually
        // renders. "Complete" means the UI has no remaining step to show.
        // This used to key off `onboarding_state.billing` directly, which
        // drifted out of sync with the UI (e.g. `billing` could flip true
        // after KYC without the user having picked a plan).
        const accountReady = this.hasCompletedAccountInformation();
        const kycReady = this.hasCompletedKYC();
        const planReady = this.hasCompletedPlan();
        const supportReady = this.hasCompletedSupport();
        const billingReady = !this.shouldShowPlanAndBilling() || !!this.customerDetails()?.onboarding_state?.billing;

        this.onboardingComplete.set(accountReady && kycReady && planReady && supportReady && billingReady);
    }

    async getStripeKYCUrl(returnPath: '/account' | '/home' = '/account'): Promise<string> {
        const stripeKYCUrl = '';
        const returnUrl = window.location.origin + returnPath;
        await lastValueFrom(this.http.get(`${environment.baseUrl}/api_account/stripe_kyc_link?success_url=${returnUrl}&failure_url=${returnUrl}`))
            .then((res: any) => {
                if (!res?.url) throw new Error('onboarding/stripe-kyc-link/no-url');
                window.open(res.url, '_self');
            })
            .catch(err => {
                console.error(err);
                throw err;
            });
        return stripeKYCUrl;
    }
}
